import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, TextField, Typography } from "@material-ui/core";
import api from "../../services/api";
import useCoachStyles from "../MessageCoachNotesDialog/coachStyles";

export const outcomes = { corrected: "Corregido", clarified: "Aclarado", no_action: "No requiere accion", wrong_recipient: "Destinatario equivocado" };
const date = value => value ? new Date(value).toLocaleString("es-CO", { timeZone: "America/Bogota" }) : "Sin fecha limite";
const localDate = value => value ? new Date(new Date(value).getTime() - 5 * 3600000).toISOString().slice(0, 16) : "";
const describe = item => {
  const d = item.detail || {};
  switch (item.action) {
    case "created": return `Anotacion enviada a ${d.targetName || d.targetUserId}`;
    case "reply_created": return `Respuesta enviada a ${d.targetName || d.targetUserId}`;
    case "reply_received": return `Nueva respuesta #${d.noteId}`;
    case "read": return "El destinatario registro su lectura";
    case "resolved": return `Cierre: ${outcomes[d.outcome] || d.outcome}. ${d.comment || ""}`;
    case "reassigned": return `Destinatario anterior #${d.previousTargetUserId}; nuevo: ${d.targetName}. Motivo: ${d.reason}`;
    case "settings": return `Gestion actualizada: ${d.after?.coachPriority}, ${d.after?.coachTopic}, ${date(d.after?.coachDueAt)}`;
    default: return item.action;
  }
};

export default function CoachActionsDialog({ noteId, currentUser, onClose, onChanged }) {
  const classes = useCoachStyles();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [priority, setPriority] = useState("normal");
  const [dueAt, setDueAt] = useState("");
  const [topic, setTopic] = useState("General");
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState("");
  const [comment, setComment] = useState("");
  const admin = ["admin", "super"].includes(currentUser?.profile);
  const load = async () => {
    const response = await api.get(`/coach-actions/notes/${noteId}/history`);
    const metadata = response.data.note.metadataJson || {};
    setData(response.data);
    setPriority(metadata.coachPriority || "normal");
    setDueAt(localDate(metadata.coachDueAt));
    setTopic(metadata.coachTopic || "General");
    setTarget(String(response.data.note.targetUserId || ""));
  };
  useEffect(() => {
    let active = true;
    setBusy(true);
    Promise.all([load(), admin ? api.get("/users/list").then(r => { if (active) setUsers(Array.isArray(r.data) ? r.data : []); }) : Promise.resolve()])
      .catch(() => { if (active) setError("No se pudo cargar la gestion. Cierre y vuelva a abrir."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [noteId, currentUser?.id, currentUser?.companyId]);
  const save = async (action, body) => {
    setBusy(true); setError("");
    try {
      await api.post(`/coach-actions/notes/${noteId}/${action}`, { ...body, expectedUpdatedAt: data.note.updatedAt });
      window.dispatchEvent(new Event("coach-updated"));
      onChanged?.();
      await load();
      setReason("");
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar. Revise la informacion antes de reintentar.");
      if (err.response?.status === 409) await load().catch(() => {});
    } finally { setBusy(false); }
  };
  const open = data?.note.status !== "resolved";
  const field = { fullWidth: true, variant: "outlined", margin: "dense", disabled: busy };
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ className: classes.root }}>
    <DialogTitle>Gestion e historial · Anotacion #{noteId}</DialogTitle>
    <DialogContent dividers>
      {busy && <CircularProgress size={24} aria-label="Guardando gestion" />}
      {error && <Typography role="alert" color="error">{error}</Typography>}
      {data && <>
        <Typography>De {data.note.authorUser?.name} para {data.note.targetUser?.name || "Sin destinatario"}</Typography>
        <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginBottom: 16 }}>{data.note.note}</Typography>
        {admin && open && <>
          <Typography variant="h6">Prioridad y plazo</Typography>
          <TextField {...field} select label="Prioridad" value={priority} onChange={e => setPriority(e.target.value)}>{[["normal", "Normal"], ["important", "Importante"], ["urgent", "Urgente"]].map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}</TextField>
          <TextField {...field} id="coach-due-at" label="Fecha limite (Colombia)" type="datetime-local" InputLabelProps={{ shrink: true }} value={dueAt} onChange={e => setDueAt(e.target.value)} />
          <TextField {...field} id="coach-topic" label="Tema" inputProps={{ maxLength: 80 }} value={topic} onChange={e => setTopic(e.target.value)} />
          <Button variant="contained" color="primary" disabled={busy || !topic.trim()} onClick={() => save("settings", { priority, dueAt: dueAt ? `${dueAt}:00-05:00` : null, topic })}>Guardar gestion</Button>
          <Divider style={{ margin: "20px 0" }} />
          <Typography variant="h6">Corregir destinatario</Typography>
          <Typography variant="body2">Se conserva el destinatario anterior y el motivo. No se cambia una intervencion que ya tenga respuestas.</Typography>
          <TextField {...field} select label="Nuevo destinatario" value={target} onChange={e => setTarget(e.target.value)}>
            {target && !users.some(u => String(u.id) === target) && <MenuItem value={target}>{data.note.targetUser?.name || `Usuario #${target}`}</MenuItem>}
            {users.filter(u => Number(u.id) !== Number(data.note.authorUserId)).map(u => <MenuItem key={u.id} value={String(u.id)}>{u.name} {u.email ? `(${u.email})` : ""}</MenuItem>)}
          </TextField>
          <TextField {...field} id="coach-reassign-reason" label="Motivo de la correccion" multiline inputProps={{ maxLength: 2000 }} value={reason} onChange={e => setReason(e.target.value)} />
          <Button variant="outlined" disabled={busy || data.replies > 0 || reason.trim().length < 5 || !target || Number(target) === Number(data.note.targetUserId)} onClick={() => save("reassign", { targetUserId: Number(target), reason })}>Confirmar cambio de destinatario</Button>
          {data.replies > 0 && <Typography variant="caption" display="block">Ya tiene respuestas. Si el error esta en una respuesta posterior, abra su propia gestion desde el hilo.</Typography>}
        </>}
        <Divider style={{ margin: "20px 0" }} />
        <Typography variant="h6">{open ? "Cerrar con resultado" : "Resultado del cierre"}</Typography>
        {open ? <>
          <TextField {...field} select label="Resultado" value={outcome} onChange={e => setOutcome(e.target.value)}>{Object.entries(outcomes).map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}</TextField>
          <TextField {...field} id="coach-resolution-comment" label="Comentario del cierre" multiline inputProps={{ maxLength: 2000 }} value={comment} onChange={e => setComment(e.target.value)} />
          <Typography variant="caption" display="block">Cierra esta anotacion, no las demas intervenciones del hilo. Leer o responder no equivale a resolver.</Typography>
          <Button variant="contained" color="primary" disabled={busy || !outcome} onClick={() => save("resolve", { outcome, comment })}>Confirmar cierre</Button>
        </> : <Typography>{outcomes[data.note.metadataJson?.coachOutcome] || "Cierre anterior sin resultado registrado"} {data.note.metadataJson?.coachResolutionComment}</Typography>}
        <Divider style={{ margin: "20px 0" }} />
        <Typography variant="h6">Historial</Typography>
        <Typography variant="caption">El historial detallado registra acciones hechas con esta nueva gestion. No reconstruye acciones antiguas.</Typography>
        {!data.events.length && <Typography>Sin eventos registrados.</Typography>}
        {[...data.events].reverse().map((item, index) => <div key={index} style={{ borderLeft: "3px solid #6ca8cd", paddingLeft: 12, marginTop: 12 }}><Typography variant="body2">{describe(item)}</Typography><Typography variant="caption">{item.actor} · {date(item.at)}</Typography></div>)}
      </>}
    </DialogContent>
    <DialogActions><Button variant="outlined" disabled={busy} onClick={onClose}>Cerrar</Button></DialogActions>
  </Dialog>;
}
